import '@/pixel/style/style.css';
import '@/styles/base/base.css';

import 'isomorphic-unfetch';
import 'lazysizes';
import { importScript } from '@/components/commons/utils';

import { createSSRApp } from 'vue';
import { createStore } from 'vuex';

import { createHead } from '@vueuse/head';
import Cookies from 'cookie-universal';

import Tracking from '@/components/commons/Tracking.js';
import { createRouter } from '@/router';
import UserService from '@/services/UserService';
import LocationService from '@/services/LocationService';
import App from '@/components/App.vue';
import { init as initExperiments } from '@/compositions/useExperiment.js';

if (!import.meta.env.SSR) {
  init();
}

function initTracking() {
  if (window.articleTrackingLoaded) {
    return false;
  }

  // Ensure tracking is not loaded more than once
  window.articleTrackingLoaded = true;

  Tracking.loadGoogleAnalytics();
  Tracking.loadGoogleTagManager();

  ga('send', 'pageview');

  Tracking.exposeGeoData();

  window.removeEventListener('scroll', initTrackingOnEvent);
  window.removeEventListener('mousemove', initTrackingOnEvent);
  window.removeEventListener('touchstart', initTrackingOnEvent);
}

function initTrackingOnEvent(event) {
  initTracking();
  event?.currentTarget?.removeEventListener(event.type, initTrackingOnEvent);
}

function init() {
  window.lazySizesConfig = window.lazySizesConfig || {
    loadMode: 1,
  };

  window._fbevents =
    window._fbevents ||
    function () {
      _fbevents.q = _fbevents.q || [];
      if (arguments.length > 0) {
        const obj = arguments[0];
        if (obj.value === undefined) {
          obj.value = {};
        }
        obj.value.action_source = 'website';
        obj.value.event_source_url = window.location.href;
        obj.value.user_data = {
          client_user_agent: window.navigator.userAgent
        }
        if (typeof fbq != 'undefined') {
          if (obj.eventID !== 'undefined') {
            fbq(obj.type, obj.event, obj.value, obj.eventID);
          } else {
            fbq(obj.type, obj.event, obj.value);
          }
        } else {
          _fbevents.q.push(obj);
        }
      }
    };

  Tracking.init();
  initExperiments();

  Cookies().remove('orderID');

  window.addEventListener('DOMContentLoaded', () => {
    window.setTimeout(() => {
      initTracking();
      if (!window.Stripe) {
        importScript('https://js.stripe.com/v3');
      }
    }, 4500);
  });

  window.addEventListener('scroll', initTrackingOnEvent);
  window.addEventListener('mousemove', initTrackingOnEvent);
  window.addEventListener('touchstart', initTrackingOnEvent);

  UserService.getUser();

  window.addEventListener('error', (error) => {
    Tracking.sentryCaptureError(error);
  });
}

// eslint-disable-next-line import/prefer-default-export
export function createApp(context = {}) {
  const store = createStore();
  const router = createRouter();
  const head = createHead();

  const { cookies } = context;
  if (cookies) {
    // Override / use client cookies on server
    Object.keys(cookies).forEach((cookieKey) => {
      Cookies().set(cookieKey, cookies[cookieKey]);
    });
  }

  LocationService.getGeoLocation();
  LocationService.getZipLocation();

  const app = createSSRApp(App);

  app.use(router);
  app.use(store);
  app.use(head);

  return { app, router, store, head };
}
